import React, { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react';
import {
	Alert,
	Form,
	Input,
	Button,
	Select,
	Switch,
	Typography,
	Card,
	Row,
	Col,
	Space,
	Modal,
	message,
	Checkbox,
} from 'antd';
import { v4 as uuidv4 } from 'uuid';
import { DeleteOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import {
	uploadToS3,
	downloadFromS3Signed,
	parse,
} from 'src/_shared/services/utils.js';
import { Editor } from '@hugerte/hugerte-react';

import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { reorder } from '@atlaskit/pragmatic-drag-and-drop/reorder';

import { FormElementsSidebar } from 'src/new-form-builder/FormElementsSidebar';
import { FormArea } from 'src/new-form-builder/FormAreaComponent';
import { FormMetadataSettings } from 'src/new-form-builder/FormMetadataSettings';
import FormPreview from 'src/new-form-builder/FormPreview';

import { FormBuilderContext } from 'src/new-form-builder/FormBuilderContext';
import { createRegistry } from 'src/new-form-builder/createRegistry';

const { Title } = Typography;
const { Option } = Select;

const ELEMENT_TYPES = {
	INPUT: 'input',
	TEXTAREA: 'textarea',
	SELECT: 'select',
	LABEL: 'label',
	LINEBREAK: 'linebreak',
	// Commented out until we have time to add new form elements
	//SWITCH: 'switch'
};
const formTypes = ['general', 'referral', 'acceptance', 'interested', 'custom'];

// Results of loading forms from S3. A genuine 404 is the only case where it
// is safe to seed drafts from the legacy referral questions — any other
// failure means forms may exist that we just couldn't read, and saving over
// them would destroy them.
const LOAD_OK = 'ok';
const LOAD_NOT_FOUND = 'not-found';
const LOAD_ERROR = 'error';

const formTypeHeaders = {
	referral: {
		title: 'Referral Form',
		description:
			'Add custom questions for your employees to answer when making a referral.',
	},
	acceptance: {
		title: 'Acceptance Form',
		description:
			'Add custom questions to Acceptance Form for candidates to answer when accepting a referral.',
	},
	interested: {
		title: "I'm Interested Form",
		description:
			'Add custom questions to the "I\'m Interested" Form for applicants to answer when completing their self-referral.',
	},
	general: {
		title: 'General/Lead Form',
		description:
			'Add custom questions for your employees to answer when making a General Referral or Referral Lead.',
	},
	custom: {
		title: 'New Custom Form (Click on text to edit title)',
		description:
			'Add custom questions and elements to a generic form that can be connected to the application using a Form Connection Identifier. (Click on text to edit)',
	},
};

const DEFAULT_FORM_METADATA = {
	askForResume: {
		formTypes: ['referral', 'general', 'interested', 'acceptance'],
		defaultValue: false
	},
	resumeRequired: {
		formTypes: ['referral', 'general', 'interested', 'acceptance'],
		defaultValue: false
	},
	askForPhoneNumber: {
		formTypes: ['acceptance'],
		defaultValue: false
	},
	showUserEmail: {
		formTypes: ['interested'],
		defaultValue: false
	},
	formConnectionId: {
		formTypes: ['custom'],
		defaultValue: ""
	},
	title: {
		formTypes: ['custom'],
		defaultValue: "New Custom Form (Click to edit title)"
	},
	description: {
		formTypes: ['custom'],
		defaultValue: `Click on this text to edit the description. 
Custom forms are generic forms that can be connected to 
the application using a Form Connection Identifier.`
	}
};
const getDefaultMetadata = (formType = 'referral') => {
	const metadata = {};
	Object.keys(DEFAULT_FORM_METADATA).forEach((key) => {
		if (DEFAULT_FORM_METADATA[key].formTypes.includes(formType)) {
			metadata[key] = DEFAULT_FORM_METADATA[key].defaultValue;
		}
	});

	if (formType === 'custom') {
		metadata.id = uuidv4();
	}

	return metadata;
};
const createDefaultElement = (type) => {
	const idForRendering = uuidv4();
	switch (type) {
		case ELEMENT_TYPES.INPUT:
			return {
				id: idForRendering,
				type,
				name: `field_${idForRendering}`,
				label: 'Text Input Label Placeholder',
				placeholder: 'Enter text',
			};
		case ELEMENT_TYPES.TEXTAREA:
			return {
				id: idForRendering,
				type,
				name: `field_${idForRendering}`,
				label: 'Multi-Line Input Label Placeholder',
				placeholder: 'Enter text',
			};
		case ELEMENT_TYPES.SELECT:
			return {
				id: idForRendering,
				type,
				name: `field_${idForRendering}`,
				label: 'Dropdown Label Placeholder',
				placeholder: 'Select an option',
				options: [
					{ label: 'Option 1', value: 'option_1' },
					{ label: 'Option 2', value: 'option_2' },
				],
			};
		case ELEMENT_TYPES.LABEL:
			return {
				id: idForRendering,
				type,
				name: `field_${idForRendering}`,
				content: 'Rich Text Placeholder',
			};
		// Commented out until we have time to add new form elements
		// case ELEMENT_TYPES.SWITCH:
		//   return {
		//     ...baseElement,
		//     label: 'Switch Label Placeholder',
		//   };
		case ELEMENT_TYPES.LINEBREAK:
		default:
			return {type, id: idForRendering, name: `field_${idForRendering}`}
	}
};
const createEmptyFormDrafts = () => {
	const drafts = {};
	formTypes.forEach((type) => {
		if (type === 'custom') {
			drafts[type] = [{
				elements: [],
				metadata: getDefaultMetadata(type),
			}];
		} else {
			drafts[type] = { elements: [], metadata: getDefaultMetadata(type) };
		}
	});
	return drafts;
};
const normalizeFormState = ({formState = {}, formKey}) => {
	const elements = formState?.elements || [];
	const metadata = formState?.metadata || {};
	const baseMetadata = getDefaultMetadata(formKey);
	const normalizedElements = elements.filter((element) => {
		if (!element?.id) {
			element.id = uuidv4();
		}
		return Object.values(ELEMENT_TYPES).includes(element.type);
	});
	const normalizedMetadata = Object.keys(metadata).reduce((acc, key) => {
		if (baseMetadata[key]) {
			acc[key] = metadata[key];
		}
		return acc;
	}, {});

	if (formKey === 'custom') {
		normalizedMetadata.id = metadata.id || uuidv4();
	}

	return {
		elements: normalizedElements,
		metadata: normalizedMetadata
	}
}

const ElementEditor = memo(({ element, onSave, visible, onCancel }) => {
	const [form] = Form.useForm();
	const [isFormValid, setIsFormValid] = useState(true);
	const [editorContent, setEditorContent] = useState(
		element?.type === ELEMENT_TYPES.LABEL ? element?.content || '' : '',
	);
	const [editorKey, setEditorKey] = useState(
		element?.type === ELEMENT_TYPES.LABEL ? uuidv4() : null,
	);
	const [editorValue, setEditorValue] = useState(
		element?.type === ELEMENT_TYPES.LABEL ? element?.content || '' : '',
	);

	useEffect(() => {
		if (visible && element) {
			form.setFieldsValue(element);
			if (element.type === ELEMENT_TYPES.LABEL) {
				setEditorContent(element.content || '');
			}
		}
	}, [form, element, visible]);

	const handleFieldsChange = () => {
		const hasErrors = form
			.getFieldsError()
			.some(({ errors }) => errors.length > 0);
		setIsFormValid(!hasErrors);
	};

	const handleSave = () => {
		form.validateFields().then((values) => {
			onSave({ ...element, ...values, content: editorContent });
			form.resetFields();
		});
	};

	const handleEditorChange = (content) => {
		setEditorContent(content || '');
		form.setFieldsValue({ content: content || '' });
	};

	const renderFormFields = () => {
		if (!element) return null;

		const commonFields = (
			<React.Fragment key={uuidv4()}>
				{element.type !== ELEMENT_TYPES.LINEBREAK &&
					element.type !== ELEMENT_TYPES.LABEL && (
						<Form.Item name="label" label="Label">
							<Input />
						</Form.Item>
					)}
				{element.type !== ELEMENT_TYPES.LINEBREAK &&
					element.type !== ELEMENT_TYPES.LABEL && (
						<Form.Item
							name="required"
							label="Required"
							valuePropName="checked"
						>
							<Switch />
						</Form.Item>
					)}
			</React.Fragment>
		);

		switch (element.type) {
			case ELEMENT_TYPES.INPUT:
			case ELEMENT_TYPES.TEXTAREA:
				return (
					<React.Fragment key={uuidv4()}>
						{commonFields}
						<Form.Item name="placeholder" label="Placeholder">
							{element.type === ELEMENT_TYPES.TEXTAREA ? (
								<Input.TextArea />
							) : (
								<Input />
							)}
						</Form.Item>
					</React.Fragment>
				);
			case ELEMENT_TYPES.SELECT:
				return (
					<React.Fragment key={uuidv4()}>
						{commonFields}
						<Form.Item name="placeholder" label="Placeholder">
							<Input />
						</Form.Item>
						<div className="ant-col ant-form-item-label">
							<label>Options</label>
						</div>
						<Form.List name="options">
							{(fields, { add, remove }) => (
								<>
									{fields.map((field) => (
										<Row key={field.key} gutter={8}>
											<Col span={20}>
												<Form.Item
													{...field}
													name={[field.name, 'label']}
													fieldKey={[
														field.fieldKey,
														'label',
													]}
													rules={[
														{
															required: true,
															message:
																'Missing label',
														},
													]}
												>
													<Input placeholder="Option Label" />
												</Form.Item>
												<Form.Item
													{...field}
													name={[field.name, 'value']}
													fieldKey={[
														field.fieldKey,
														'value',
													]}
													hidden={true}
													preserve={true}
												/>
											</Col>
											<Col span={4}>
												<Button
													danger
													onClick={() =>
														remove(field.name)
													}
													icon={<DeleteOutlined />}
												/>
											</Col>
										</Row>
									))}
									<Form.Item>
										<Button
											type="dashed"
											onClick={() => {
												const nextOptionNumber =
													fields.length + 1;
												add({
													label: '',
													value: `option_${nextOptionNumber}`,
												});
											}}
											block
											icon={<PlusOutlined />}
										>
											Add Option
										</Button>
									</Form.Item>
								</>
							)}
						</Form.List>
					</React.Fragment>
				);
			case ELEMENT_TYPES.LABEL:
				return (
					<React.Fragment key="label-editor">
						<Editor
							key={editorKey}
							init={{
								height: 350,
								menubar: false,
								branding: false,
								plugins: ['quickbars', 'lists'],
								toolbar:
									'undo redo fontsizeinput bold italic forecolor backcolor | \
                        styles alignleft aligncenter alignright alignjustify',

								placeholder: 'Enter text',
								quickbars_selection_toolbar:
									'bold italic forecolor backcolor removeformat',
								quickbars_image_toolbar: false,
								quickbars_insert_toolbar: false,
								toolbar_mode: 'floating',
							}}
							value={editorContent}
							onEditorChange={(newValue, editor) => {
								setEditorValue(newValue);
								handleEditorChange(
									editor.getContent({ format: 'html' }),
								);
							}}
						/>
					</React.Fragment>
				);
			// Commented out until we have time to add new form elements
			// case ELEMENT_TYPES.SWITCH:
			//   return (
			//     <>
			//       {commonFields}
			//     </>
			//   );
			default:
				return commonFields;
		}
	};

	return (
		<Modal
			title="Edit Element Properties"
			open={visible}
			onOk={handleSave}
			onCancel={onCancel}
			maskClosable={false}
      destroyOnClose={true}
			okButtonProps={{ disabled: !isFormValid }}
			centered
		>
			<Form
				form={form}
				layout="vertical"
				onFieldsChange={handleFieldsChange}
			>
				{renderFormFields()}
			</Form>
		</Modal>
	);
});

export default function FormBuilderApp(props) {
	const [data, setData] = useState(() => ({
		elements: [],
	}));
	const [userGroups, setUserGroups] = useState(props.userGroups || []);
	const [editingElement, setEditingElement] = useState(null);
	const [editingIndex, setEditingIndex] = useState(null);
	const [isEditorVisible, setIsEditorVisible] = useState(false);
	const [formType, setFormType] = useState('referral');
	const [selectedCustomFormIndex, setSelectedCustomFormIndex] = useState(0);

	const [useUniqueFormsForGroups, setUseUniqueFormsForGroups] =
		useState(false);
	const [selectedUserGroup, setSelectedUserGroup] = useState(null);
	const [formDrafts, setFormDrafts] = useState({
		main: createEmptyFormDrafts(),
		userGroups: {},
	});
	
	const isSuperAdmin = props.currentUser?.role === 'superAdmin';
	const [formMetadata, setFormMetadata] = useState(getDefaultMetadata());
	const [loadedFromS3, setLoadedFromS3] = useState({
		main: false,
		userGroups: {},
	});
	const [s3LoadFailed, setS3LoadFailed] = useState({
		main: false,
		userGroups: {},
	});
	const [seededFromLegacy, setSeededFromLegacy] = useState(false);
	// Only the scope (main vs selected user group) whose file could not be
	// read needs the overwrite warning — that is the only file a save would
	// overwrite. Saving stays enabled either way; a failed load just adds a
	// confirmation step, because a load error can also mean the company has
	// never saved forms.
	const currentScopeLoadFailed = !useUniqueFormsForGroups
		? s3LoadFailed.main
		: Boolean(
				selectedUserGroup && s3LoadFailed.userGroups[selectedUserGroup],
			);

	const currentUser = props.currentUser;
	const allMultiLingualData = props.allMultiLingualData;
	const legacyReferralQuestions = props.legacyReferralQuestions || [];

	const stableData = useRef(data);
	useEffect(() => {
		stableData.current = data;
	}, [data]);

	const [registry] = useState(createRegistry);

	const [instanceId] = useState(() => `form-builder-${uuidv4()}`);

	const getElements = useCallback(() => {
		return stableData.current.elements;
	}, []);

	useEffect(() => {
		const loadInitialForms = async () => {
			try {
				const loadResult = await loadFormsFromS3(null);

				// Legacy rows scoped to a sub-company (affiliate flows) belong
				// only to that flow, never to the main company forms; inactive
				// rows are retired. Neither may migrate into the new forms.
				const mainCompanyLegacyQuestions = (
					legacyReferralQuestions || []
				).filter((item) => !item.subCompanyId && item.active !== false);

				if (
					loadResult === LOAD_NOT_FOUND &&
					mainCompanyLegacyQuestions.length
				) {
					const groupedLegacyQuestions = {
						acceptance: mainCompanyLegacyQuestions.filter(
							(item) => item.isCandidate,
						) || [],
						interested: mainCompanyLegacyQuestions.filter(
							(item) => !item.isCandidate && item.isInterested,
						) || [],
						referral: mainCompanyLegacyQuestions.filter(
							(item) =>
								!item.isCandidate &&
								!item.isInterested &&
								!item.isGeneral,
						) || [],
						general: mainCompanyLegacyQuestions.filter(
							(item) =>
								!item.isCandidate &&
								!item.isInterested &&
								item.isGeneral,
						) || [],
					}

					const legacyFormsToDraft = importOldFormatWithMetadata(groupedLegacyQuestions);

					setFormDrafts((prevDrafts) => ({
						...prevDrafts,
						main: {
							...prevDrafts.main,
							...legacyFormsToDraft
						},
					}));

					setData((prevData) => ({
						...prevData,
						elements: legacyFormsToDraft?.referral?.elements || [],
					}));
					setFormType('referral');
					setFormMetadata(
						legacyFormsToDraft?.referral?.metadata || getDefaultMetadata(),
					);
					setSeededFromLegacy(true);
				}

				// Handle prefill query params (e.g. from nomination form creation)
				const searchParams = new URLSearchParams(props.location?.search);
				const prefillConnectionId = searchParams.get('prefillConnectionId');
				const prefillTitle = searchParams.get('prefillTitle');
				const prefillDescription = searchParams.get('prefillDescription');

				if (prefillConnectionId && prefillTitle) {
					const prefillMetadata = {
						...getDefaultMetadata('custom'),
						formConnectionId: prefillConnectionId,
						title: prefillTitle,
						description: prefillDescription || getDefaultMetadata('custom').description,
					};

					const newCustomForm = {
						elements: [],
						metadata: prefillMetadata,
					};

					setFormDrafts((prevDrafts) => {
						const customForms = [...(prevDrafts.main?.custom || [])];
						customForms.push(newCustomForm);
						const newIndex = customForms.length - 1;

						// Set the UI to show the new custom form
						setSelectedCustomFormIndex(newIndex);
						setFormType('custom');
						setData({ elements: [] });
						setFormMetadata(prefillMetadata);

						return {
							...prevDrafts,
							main: {
								...prevDrafts.main,
								custom: customForms,
							},
						};
					});
				}

				const customFormId = searchParams.get('customFormId');
				if (customFormId) {
					setFormDrafts((prevDrafts) => {
						const customForms = prevDrafts.main?.custom || [];
						const matchIndex = customForms.findIndex(
							(form) => form?.metadata?.id === customFormId
						);

						if (matchIndex !== -1) {
							setSelectedCustomFormIndex(matchIndex);
							setFormType('custom');
							setData({ elements: customForms[matchIndex]?.elements || [] });
							setFormMetadata(customForms[matchIndex]?.metadata || getDefaultMetadata('custom'));
						}

						return prevDrafts;
					});
				}
			} catch (error) {
				console.error('Error loading initial forms:', error);
			}
		};

		loadInitialForms();
	}, []);

	const reorderElement = useCallback(
		({ elementId, startIndex, finishIndex }) => {
			setData((data) => {
				return {
					...data,
					elements: reorder({
						list: data.elements,
						startIndex,
						finishIndex,
					}),
				};
			});
		},
		[],
	);

	useEffect(() => {
		return combine(
			monitorForElements({
				canMonitor({ source }) {
					return source.data.instanceId === instanceId;
				},
				onDrop(args) {
					const { location, source } = args;
					const dropTarget = location.current.dropTargets?.[0];
					const edge = dropTarget?.data
						? extractClosestEdge(dropTarget.data)
						: null;
					if (!location.current.dropTargets.length || !edge) {
						return;
					}

					if (source.data.type === 'sidebar-element') {
						const elementType = source.data.elementType;
						const elementsCount =
							stableData.current.elements.length;
						let insertIndex = elementsCount;
						if (dropTarget.data.type === 'form-element') {
							const targetIndex = dropTarget.data.index;
							insertIndex =
								edge === 'bottom'
									? targetIndex + 1
									: targetIndex;
						} else if (dropTarget.data.type === 'form-area') {
							insertIndex = edge === 'bottom' ? elementsCount : 0;
						}
						addElement(elementType, insertIndex);
					}
				},
			}),
		);
	}, [instanceId, reorderElement]);

	const contextValue = useMemo(() => {
		return {
			getElements,
			reorderElement,
			registerElement: registry.registerElement,
			instanceId,
		};
	}, [getElements, instanceId, reorderElement, registry.registerElement]);

	const addElement = useCallback((elementType, position = null) => {
		const newElement = createDefaultElement(elementType);
		setData((data) => {
			const newElements = [...data.elements];
			if (
				position !== null &&
				position >= 0 &&
				position <= newElements.length
			) {
				newElements.splice(position, 0, newElement);
			} else {
				newElements.push(newElement);
			}

			return {
				...data,
				elements: newElements,
			};
		});
	}, []);

	const removeElement = useCallback((index) => {
		setData((data) => ({
			...data,
			elements: data.elements.filter((_, i) => i !== index),
		}));
	}, []);

	const duplicateElement = useCallback((index) => {
		setData((data) => {
			const newElements = [...data.elements];
			const duplicated = {
				...newElements[index],
				id: uuidv4(),
				name: `${newElements[index].name}_copy_${Math.floor(Math.random() * 1000)}`,
			};
			newElements.splice(index + 1, 0, duplicated);

			return {
				...data,
				elements: newElements,
			};
		});
	}, []);

	const editElement = useCallback(
		(index) => {
			setEditingElement(data.elements[index]);
			setEditingIndex(index);
			setIsEditorVisible(true);
		},
		[data.elements],
	);

	const handleSaveElement = (updatedElement) => {
		setData((data) => {
			const newElements = [...data.elements];
			newElements[editingIndex] = updatedElement;

			return {
				...data,
				elements: newElements,
			};
		});

		setIsEditorVisible(false);
		setEditingElement(null);
		setEditingIndex(null);
	};

	const importOldFormatWithMetadata = (jsonData) => {
		let oldFormatItems = jsonData || {};
		let importedForms = {};
		const skippedQuestions = [];

		Object.entries(oldFormatItems).forEach(([groupKey, elementsArray = []]) => {
			const preSortedElementsArray = elementsArray.sort((a, b) => a.sortOrder - b.sortOrder);
			let newMetadata = getDefaultMetadata(groupKey);
			const newElements = [];

			for (const elementObject of preSortedElementsArray) {
				if (elementObject.sortOrder === 0) {
					const metadataObj = parse(elementObject.questions) || {};
					if (
						metadataObj['Ask for resume attachment'] !== undefined
					) {
						newMetadata.askForResume =
							!!metadataObj['Ask for resume attachment'];
					}
					if (metadataObj['Resume required'] !== undefined) {
						newMetadata.resumeRequired =
							!!metadataObj['Resume required'];
					}
					if (
						groupKey === 'interested' &&
						metadataObj["Show User's Email Address"] !== undefined
					) {
						newMetadata.showUserEmail =
							!!metadataObj["Show User's Email Address"];
					}
					continue;
				}
				if (groupKey === 'acceptance' && parse(elementObject.questions)?.text === 'Phone Number') {
					newMetadata.askForPhoneNumber = true;
					continue;
				}
				const parsedQuestion = parse(elementObject.questions);
				const convertedElement = convertToNewElementFormat(parsedQuestion);
				if (convertedElement) {
					newElements.push(convertedElement);
				} else {
					skippedQuestions.push(
						parsedQuestion?.label ||
							parsedQuestion?.text ||
							parsedQuestion?.element ||
							'Unknown question',
					);
				}
			}
			importedForms[groupKey] = {
				elements: newElements,
				metadata: newMetadata,
			};
		});

		if (skippedQuestions.length) {
			message.warning(
				`${skippedQuestions.length} legacy question(s) use element types this builder does not support yet and were NOT imported: ${skippedQuestions.join(', ')}. They will be missing from the forms if you save.`,
				10,
			);
		}

		return importedForms;
	};

	const convertToNewElementFormat = (questionsObj) => {
		try {
			const elementType = questionsObj.element;
			switch (elementType) {
				case 'TextInput':
					return {
						name: `${questionsObj?.field_name?.replace('text_input_', 'field_')?.toLowerCase() || 'field_' + uuidv4()}`,
						type: ELEMENT_TYPES.INPUT,
						label:
							questionsObj.label ||
							questionsObj.text ||
							'Input Field',
						placeholder: 'Enter text',
						importedFromLegacy: true,
						required: questionsObj.required || false,
					};
				case 'TextArea':
					return {
						name: `${questionsObj?.field_name?.replace('text_area_', 'field_')?.toLowerCase() || 'field_' + uuidv4()}`,
						type: ELEMENT_TYPES.TEXTAREA,
						label:
							questionsObj.label ||
							questionsObj.text ||
							'Text Area',
						placeholder: 'Enter text',
						importedFromLegacy: true,
						required: questionsObj.required || false,
					};
				case 'Dropdown':
					return {
						name: `${questionsObj?.field_name?.replace('dropdown_', 'field_')?.toLowerCase() || 'field_' + uuidv4()}`,
						type: ELEMENT_TYPES.SELECT,
						label:
							questionsObj.label || questionsObj.text || 'Select',
						placeholder: 'Select an option',
						importedFromLegacy: true,
						required: questionsObj.required || false,
						options: Array.isArray(questionsObj.options)
							? questionsObj.options.map((opt) => {
									const label =
										typeof opt === 'object'
											? opt.text || opt.label
											: opt;
									const value =
										typeof opt === 'object'
											? opt.value
											: typeof opt === 'string'
												? opt
														.toLowerCase()
														.replace(/\s+/g, '_')
												: `option_${Math.random().toString(36).substr(2, 9)}`;
									return { label, value };
								})
							: [],
					};
				case 'Label':
					return {
						name: `field_${questionsObj?.id?.toLowerCase() || uuidv4()}`,
						type: ELEMENT_TYPES.LABEL,
						content:
							questionsObj.content ||
							questionsObj.text ||
							'Label Content',
						importedFromLegacy: true,
					};
				case 'LineBreak':
					return {
						type: ELEMENT_TYPES.LINEBREAK,
					};
				// Commented out until we have time to add new form elements
				// case 'Switch':
				//   return {
				//     ...baseElement,
				//     type: ELEMENT_TYPES.SWITCH,
				//     label: questionsObj.text || questionsObj.question || 'Switch'
				//   };
				default:
					console.warn(
						`Unknown element type: ${elementType || JSON.stringify(questionsObj)}`,
					);
					return null;
			}
		} catch (e) {
			console.error('Error converting to new element format:', e);
			return null;
		}
	};
	const generateModernJsonFormat = (externalModernJson = null) => {
		try {
			saveCurrentFormState();
			
			const modernJson = externalModernJson || {
				companyId: currentUser.companyId,
				lastUpdated: new Date().toISOString(),
				lastUpdatedByUserId: currentUser.id,
				lastUpdatedByUserEmail: currentUser.emailAddress,
				isMainFormGroup: !useUniqueFormsForGroups,
				userGroupId: useUniqueFormsForGroups ? selectedUserGroup : null,
				forms: useUniqueFormsForGroups
					? formDrafts.userGroups[selectedUserGroup]
					: formDrafts.main
			};

			if (!Array.isArray(modernJson?.forms?.custom)) {
				modernJson.forms.custom = [{
					metadata: getDefaultMetadata('custom'),
					elements: [],
				}];
			}
			
			if (Array.isArray(modernJson?.forms?.custom)) {
				modernJson.forms.custom = modernJson.forms.custom.map((form, index) => {
					if (!form.metadata) {
						form.metadata = getDefaultMetadata('custom');
					}
					
					if (!isSuperAdmin) {
						const originalForms = useUniqueFormsForGroups
							? formDrafts.userGroups[selectedUserGroup]?.custom || []
							: formDrafts.main?.custom || [];
						
						const originalForm = originalForms[index];
						
						if (originalForm && originalForm.metadata) {
							form.metadata.title = originalForm.metadata.title || DEFAULT_FORM_METADATA.title.defaultValue;
							form.metadata.description = originalForm.metadata.description || DEFAULT_FORM_METADATA.description.defaultValue;
							form.metadata.formConnectionId = originalForm.metadata.formConnectionId || "";
						}
					} else {
						if (!form.metadata.formConnectionId) {
							form.metadata.formConnectionId = "";
						}
						if (!form.metadata.title) {
							form.metadata.title = DEFAULT_FORM_METADATA.title.defaultValue;
						}
						if (!form.metadata.description) {
							form.metadata.description = DEFAULT_FORM_METADATA.description.defaultValue;
						}
					}
					
					return form;
				});
			}
			
			return modernJson;
		} catch (error) {
			console.error('Error generating modern JSON format:', error);
			return null;
		}
	};

	const performSave = async () => {
		try {
			handleFormTypeChange(formType);
			const modernJson = generateModernJsonFormat();

			if (!modernJson) {
				return;
			}

			const fileName = !useUniqueFormsForGroups
				? 'main_forms.json'
				: `${selectedUserGroup}_forms.json`;

			const jsonString = JSON.stringify(modernJson, null, 2);
			const jsonBlob = new Blob([jsonString], {
				type: 'application/json',
			});

			await uploadToS3(
				jsonBlob,
				`forms/${currentUser.companyId}/${fileName}`,
				'erin-documents',
			);

			if (!useUniqueFormsForGroups) {
				// Only a main save publishes the legacy-seeded drafts; a
				// user-group save writes a different file.
				setSeededFromLegacy(false);
			}

			// The saved file now matches local state, so the scope no longer
			// needs a load (or the failed-load overwrite warning).
			setLoadedFromS3((prev) => ({
				...prev,
				main: useUniqueFormsForGroups ? prev.main : true,
				userGroups: useUniqueFormsForGroups
					? { ...prev.userGroups, [selectedUserGroup]: true }
					: prev.userGroups,
			}));
			setS3LoadFailed((prev) => ({
				...prev,
				main: useUniqueFormsForGroups ? prev.main : false,
				userGroups: useUniqueFormsForGroups
					? { ...prev.userGroups, [selectedUserGroup]: false }
					: prev.userGroups,
			}));

			message.success('Forms saved successfully');
		} catch (error) {
			console.error('Error saving forms to S3:', error);
			message.error('Failed to save forms');
		}
	};

	const saveFormsToModernFormat = async () => {
		if (currentScopeLoadFailed) {
			Modal.confirm({
				title: 'Save without checking existing forms?',
				content:
					'Your saved forms could not be loaded, so saving will replace any forms that already exist for this view. If you have never saved forms here, it is safe to continue. To be sure, refresh the page first so your existing forms load.',
				okText: 'Save Anyway',
				okButtonProps: { danger: true },
				onOk: () => performSave(),
			});
			return;
		}

		if (seededFromLegacy && !useUniqueFormsForGroups) {
			const importedTabs = formTypes.filter(
				(type) =>
					type !== 'custom' &&
					(type === formType
						? data.elements
						: formDrafts.main?.[type]?.elements || []
					).some((element) => element?.importedFromLegacy),
			);

			Modal.confirm({
				title: 'Publish forms imported from legacy questions?',
				content: `These forms were pre-filled from your existing referral questions and saving publishes every form tab at once${
					importedTabs.length
						? ` — imported questions are on: ${importedTabs.join(', ')}`
						: ''
				}. Review each tab before saving.`,
				okText: 'Save All Forms',
				onOk: () => performSave(),
			});
			return;
		}

		await performSave();
	};

	const loadFormsFromModernFormat = (modernJson) => {
		try {
			if (!modernJson || !modernJson.forms) {
				throw new Error('Invalid modern JSON format');
			}
			setUseUniqueFormsForGroups(!modernJson.isMainFormGroup);

			if (!modernJson.isMainFormGroup && modernJson.userGroupId) {
				setSelectedUserGroup(modernJson.userGroupId);
			}

			let newFormDrafts = {
				main: { ...formDrafts.main },
				userGroups: { ...formDrafts.userGroups },
			};
			const isMainFormGroup = modernJson.isMainFormGroup;
			const userGroupId = modernJson?.userGroupId;
			const userGroups = !isMainFormGroup && userGroupId ? modernJson.forms : {};
			if (isMainFormGroup) {
				newFormDrafts = {main: {...newFormDrafts.main, ...modernJson.forms},userGroups: {...newFormDrafts.userGroups} }
				if (!newFormDrafts.main?.custom?.length) {
					newFormDrafts.main.custom = [normalizeFormState({formState: {}, formKey: 'custom'})];
				}
			}
			if (userGroups) {
				newFormDrafts.userGroups[userGroupId] = {...newFormDrafts.userGroups[userGroupId], ...userGroups};
				if (!newFormDrafts?.userGroups[userGroupId]?.custom?.length) {
					newFormDrafts.userGroups[userGroupId].custom = [normalizeFormState({formState: {}, formKey: 'custom'})];
				}
			}

			const ensureCustomFormIds = (customForms) =>
				customForms?.map((form) => {
					if (form.metadata && !form.metadata.id) {
						form.metadata.id = uuidv4();
					}
					return form;
				});

			if (newFormDrafts.main?.custom) {
				ensureCustomFormIds(newFormDrafts.main.custom);
			}
			if (newFormDrafts.userGroups?.[userGroupId]?.custom) {
				ensureCustomFormIds(newFormDrafts.userGroups[userGroupId].custom);
			}

			setFormDrafts(newFormDrafts);

			if (formType.toLowerCase() === 'custom') {
				const customForms = isMainFormGroup
					? newFormDrafts.main.custom || []
					: newFormDrafts.userGroups?.[userGroupId]?.custom || [];
				
				if (customForms.length > 0) {
					const withId = customForms[0]?.elements || [];
					withId.map((element) => {
						if (!element?.id) element.id = uuidv4();
						return element;
					})
					setData((prevData) => ({
						...prevData,
						elements: customForms[0]?.elements || []
					}));
					
					setFormMetadata(customForms[0]?.metadata || getDefaultMetadata('custom'));
					
					setSelectedCustomFormIndex(0);
				}
			} else {
				const formData = isMainFormGroup
					? newFormDrafts.main[formType]
					: newFormDrafts.userGroups?.[userGroupId]?.[formType];
				
				if (formData) {
					setData((prevData) => ({
						...prevData,
						elements: formData.elements || []
					}));
					setFormMetadata(formData.metadata || getDefaultMetadata(formType));
				}
			}

			return true;
		} catch (error) {
			console.debug(
				'Forms likely do not exist yet for this user group',
				error,
			);
			return false;
		}
	};

	const loadFormsFromS3 = async (userGroupId = null) => {
		const isMain = !userGroupId;

		if (
			(isMain && loadedFromS3.main) ||
			(!isMain && loadedFromS3.userGroups[userGroupId])
		) {
			return LOAD_OK;
		}

		const markScopeLoadFailed = (failed) => {
			setS3LoadFailed((prev) => ({
				...prev,
				main: isMain ? failed : prev.main,
				userGroups: isMain
					? prev.userGroups
					: { ...prev.userGroups, [userGroupId]: failed },
			}));
		};

		try {
			const fileName = isMain
				? 'main_forms.json'
				: `${userGroupId}_forms.json`;
			const filePath = `forms/${currentUser.companyId}/${fileName}`;

			const signedUrl = await downloadFromS3Signed(
				filePath,
				'erin-documents',
			);

			if (!signedUrl) {
				markScopeLoadFailed(true);
				return LOAD_ERROR;
			}

			const response = await fetch(signedUrl);

			// A presigned GET for a missing key answers 404 when the signer
			// role can list the bucket and 403 (AccessDenied) when it cannot,
			// so with a validly signed URL both mean "no saved forms yet".
			if (response.status === 404 || response.status === 403) {
				markScopeLoadFailed(false);
				return LOAD_NOT_FOUND;
			}

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`);
			}

			const jsonData = await response.json();
			let success = false;
			if (jsonData.forms) {
				success = loadFormsFromModernFormat(jsonData);
			}

			if (success) {
				setLoadedFromS3((prev) => ({
					...prev,
					main: isMain ? true : prev.main,
					userGroups: isMain
						? prev.userGroups
						: {
								...prev.userGroups,
								[userGroupId]: true,
							},
				}));
				markScopeLoadFailed(false);
				return LOAD_OK;
			} else {
				markScopeLoadFailed(true);
				return LOAD_ERROR;
			}
		} catch (error) {
			console.error('Error loading forms from S3:', error);
			markScopeLoadFailed(true);
			return LOAD_ERROR;
		}
	};

	const saveCurrentFormState = useCallback(() => {
		const currentFormState = {
			elements: data.elements,
			metadata: formMetadata || getDefaultMetadata(formType),
		};

		setFormDrafts((prevDrafts) => {
			const updatedDrafts = { ...prevDrafts };

			if (!useUniqueFormsForGroups) {
				if (formType.toLowerCase() === 'custom') {
					if (!Array.isArray(updatedDrafts.main.custom)) {
						updatedDrafts.main.custom = [];
					}
					
					while (updatedDrafts.main.custom.length <= selectedCustomFormIndex) {
						updatedDrafts.main.custom.push({
							elements: [],
							metadata: getDefaultMetadata('custom')
						});
					}
					
					updatedDrafts.main.custom[selectedCustomFormIndex] = currentFormState;
				} else {
					updatedDrafts.main[formType] = currentFormState;
				}
			} else if (selectedUserGroup) {
				if (!updatedDrafts.userGroups[selectedUserGroup]) {
					updatedDrafts.userGroups[selectedUserGroup] = createEmptyFormDrafts();
				}
				
				if (formType.toLowerCase() === 'custom') {
					if (!Array.isArray(updatedDrafts.userGroups[selectedUserGroup].custom)) {
						updatedDrafts.userGroups[selectedUserGroup].custom = [];
					}
					
					while (updatedDrafts.userGroups[selectedUserGroup].custom.length <= selectedCustomFormIndex) {
						updatedDrafts.userGroups[selectedUserGroup].custom.push({
							elements: [],
							metadata: getDefaultMetadata('custom')
						});
					}
					
					updatedDrafts.userGroups[selectedUserGroup].custom[selectedCustomFormIndex] = currentFormState;
				} else {
					updatedDrafts.userGroups[selectedUserGroup][formType] = currentFormState;
				}
			}

			return updatedDrafts;
		});
	}, [
		data.elements,
		formMetadata,
		formType,
		useUniqueFormsForGroups,
		selectedUserGroup,
		selectedCustomFormIndex
	]);

	const handleFormTypeChange = useCallback(
		(newType) => {
			saveCurrentFormState();
			setFormType(newType);
			
			if (newType.toLowerCase() === 'custom') {
				const customForms = !useUniqueFormsForGroups
					? formDrafts.main?.custom || []
					: formDrafts.userGroups?.[selectedUserGroup]?.custom || [];
				
				setSelectedCustomFormIndex(0);
				
				if (customForms.length > 0) {
					const firstForm = customForms[0];
					setData((prevData) => ({
						...prevData,
						elements: firstForm?.elements || []
					}));
					setFormMetadata(firstForm?.metadata || getDefaultMetadata('custom'));
				} else {
					const defaultMetadata = getDefaultMetadata('custom');
					setData((prevData) => ({
						...prevData,
						elements: []
					}));
					setFormMetadata(defaultMetadata);
				}
			} else {
				let formData;
				
				if (!useUniqueFormsForGroups) {
					formData = formDrafts.main?.[newType];
				} else if (selectedUserGroup && formDrafts.userGroups?.[selectedUserGroup]) {
					formData = formDrafts.userGroups[selectedUserGroup][newType];
				}
				
				if (formData) {
					setData((prevData) => ({
						...prevData,
						elements: formData.elements || []
					}));
					setFormMetadata(formData.metadata || getDefaultMetadata(newType));
				} else {
					setData((prevData) => ({
						...prevData,
						elements: []
					}));
					setFormMetadata(getDefaultMetadata(newType));
				}
			}
		},
		[
			formDrafts,
			useUniqueFormsForGroups,
			selectedUserGroup,
			saveCurrentFormState,
		],
	);

	const handleUniqueFormsToggle = useCallback(
		async (checked) => {
			saveCurrentFormState();
			setUseUniqueFormsForGroups(checked);

			if (checked && userGroups.length > 0) {
				setTimeout(() => {
					handleUserGroupChange(userGroups[0].id);
				}, 0);
			} else {
				if (formType === 'custom') {
					const customForms = formDrafts.main.custom || [];
					const currentCustomForm = customForms[selectedCustomFormIndex] || {
						elements: [],
						metadata: getDefaultMetadata('custom'),
					};

					setData((prevData) => ({
						...prevData,
						elements: currentCustomForm.elements || [],
					}));
					setFormMetadata(currentCustomForm.metadata || getDefaultMetadata('custom'));
				} else {
					const mainDraft = formDrafts.main[formType] || {
						elements: [],
						metadata: getDefaultMetadata(formType),
					};

					setData((prevData) => ({
						...prevData,
						elements: mainDraft.elements,
					}));
					setFormMetadata(mainDraft.metadata);
				}
				setSelectedUserGroup(null);
			}
		},
		[
			data.elements,
			formMetadata,
			formType,
			formDrafts,
			useUniqueFormsForGroups,
			selectedUserGroup,
			userGroups,
		],
	);

	const handleUserGroupChange = useCallback(
		async (userGroupId) => {
			// Save current form state before switching
			saveCurrentFormState();
			setSelectedUserGroup(userGroupId);

			if (formType === 'custom') {
				if (
					formDrafts.userGroups[userGroupId] &&
					formDrafts.userGroups[userGroupId].custom &&
					formDrafts.userGroups[userGroupId].custom.length > 0
				) {
					// Use existing drafts instead of loading from S3
					const customForms = formDrafts.userGroups[userGroupId].custom;
					
					if (Array.isArray(customForms)) {
						const currentCustomForm = customForms[selectedCustomFormIndex] || customForms[0];
						
						setData((prevData) => ({
							...prevData,
							elements: currentCustomForm.elements || [],
						}));
						setFormMetadata(
							currentCustomForm.metadata || getDefaultMetadata('custom'),
						);
					} else {
						console.error("customForms is not an array:", customForms);
						setData((prevData) => ({
							...prevData,
							elements: [],
						}));
						setFormMetadata(getDefaultMetadata('custom'));
					}
				} else {
					try {
						const loadingMessage = message.loading(
							'Loading forms...',
							0,
						);

						const formsLoaded = await loadFormsFromS3(userGroupId);
						loadingMessage();

						if (formsLoaded !== LOAD_OK) {
							setData((prevData) => ({
								...prevData,
								elements: [],
							}));
							setFormMetadata(getDefaultMetadata('custom'));

							if (!formDrafts.userGroups[userGroupId] || !formDrafts.userGroups[userGroupId].custom) {
								setFormDrafts((prevDrafts) => {
									const updatedDrafts = { ...prevDrafts };
									if (!updatedDrafts.userGroups[userGroupId]) {
										updatedDrafts.userGroups[userGroupId] = createEmptyFormDrafts();
									}
									return updatedDrafts;
								});
							}
						}
					} catch (error) {
						console.error('Error handling user group change:', error);
						message.error(
							'An error occurred while changing user groups',
						);

						setData((prevData) => ({
							...prevData,
							elements: [],
						}));
						setFormMetadata(getDefaultMetadata('custom'));
					}
				}
			} else {
				if (
					formDrafts.userGroups[userGroupId] &&
					formDrafts.userGroups[userGroupId][formType]
				) {
					const userGroupDraft =
						formDrafts.userGroups[userGroupId][formType];
					setData((prevData) => ({
						...prevData,
						elements: userGroupDraft.elements,
					}));
					setFormMetadata(
						userGroupDraft.metadata || getDefaultMetadata(formType),
					);
				} else {
					try {
						const loadingMessage = message.loading(
							'Loading forms...',
							0,
						);

						const formsLoaded = await loadFormsFromS3(userGroupId);

						loadingMessage();

						if (formsLoaded !== LOAD_OK) {
							setData((prevData) => ({
								...prevData,
								elements: [],
							}));
							setFormMetadata(getDefaultMetadata(formType));
						}
					} catch (error) {
						console.error('Error handling user group change:', error);
						message.error(
							'An error occurred while changing user groups',
						);

						setData((prevData) => ({
							...prevData,
							elements: [],
						}));
						setFormMetadata(getDefaultMetadata(formType));
					}
				}
			}
		},
		[data.elements, formDrafts, formMetadata, formType, selectedUserGroup, selectedCustomFormIndex],
	);
	const clearForm = useCallback(() => {
		if (data.elements.length > 0) {
			Modal.confirm({
				title: 'Clear Form',
				content:
					'Are you sure you want to clear the form? All unsaved changes will be lost.',
				onOk: () => {
					setData((prevData) => ({
						...prevData,
						elements: [],
					}));
					setFormMetadata(getDefaultMetadata(formType));
				},
			});
		}
	}, [data.elements.length, formType]);

	const copyFromMainForm = useCallback(() => {
		if (!useUniqueFormsForGroups || !selectedUserGroup) {
			return;
		}

		let mainForm;
		
		if (formType === 'custom') {
			const customForms = formDrafts.main.custom || [];
			mainForm = customForms[selectedCustomFormIndex] || {
				elements: [],
				metadata: getDefaultMetadata('custom'),
			};
		} else {
			mainForm = formDrafts.main[formType];
		}

		if (!mainForm || !mainForm.elements || mainForm.elements.length === 0) {
			message.info('The main form is empty. Nothing to copy.');
			return;
		}

		const clonedElements = mainForm.elements.map((element) => ({
			...element,
			id: uuidv4(),
		}));

		setData((prevData) => ({
			...prevData,
			elements: clonedElements,
		}));

		setFormMetadata({
			...mainForm.metadata,
		});

		message.success('Successfully copied elements from the main form.');
	}, [useUniqueFormsForGroups, selectedUserGroup, formDrafts.main, formType, selectedCustomFormIndex]);

	return (
		<FormBuilderContext.Provider value={contextValue}>
			<Title level={2}>Form Builder</Title>
			{currentScopeLoadFailed && (
				<Alert
					type="warning"
					showIcon
					style={{ marginBottom: '16px' }}
					message="Could not check for existing saved forms"
					description="Any forms you previously saved could not be loaded, so saving will replace them. If you have saved forms before, refresh the page so they load before you save; if this keeps happening, contact support."
				/>
			)}
			{seededFromLegacy && !currentScopeLoadFailed && (
				<Alert
					type="warning"
					showIcon
					style={{ marginBottom: '16px' }}
					message="Imported legacy questions — not published yet"
					description="These forms were pre-filled from your existing referral questions. Saving publishes every form tab at once, so review each tab before you save."
				/>
			)}
			<Row gutter={16}>
				<Col span={4}>
					<Card
						title={
							<div style={{ fontWeight: 900, fontSize: '16px' }}>
								Form Picker
							</div>
						}
						size="small"
						style={{
							marginBottom: '16px',
							borderRadius: '8px',
						}}
					>
						<div style={{ marginBottom: '16px' }}>
							<label
								style={{
									display: 'block',
									marginBottom: '8px',
								}}
							>
								Form Type:
							</label>
							<Select
								style={{ width: '100%' }}
								value={formType}
								onChange={handleFormTypeChange}
							>
								<Option value="referral">
									Referral Form
								</Option>
								<Option value="acceptance">
									Acceptance Form
								</Option>
								<Option value="interested">
									I'm Interested Form
								</Option>
								<Option value="general">
									General/Lead Form
								</Option>
								<Option value="custom">
									Custom Forms
								</Option>
							</Select>
						</div>
						{formType === 'custom' ? (
							<div style={{ marginBottom: '16px' }}>
								<label
									style={{
										display: 'block',
										marginBottom: '8px',
									}}
								>
									Custom Form:
								</label>
								<div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
									<Select
										style={{ width: '100%' }}
										value={selectedCustomFormIndex}
										dropdownRender={(menu) => (
											<>
												{menu}
												{isSuperAdmin && (
													<div style={{ padding: '8px', borderTop: '1px solid #e8e8e8' }}>
														<Button
															type="text"
															icon={<PlusOutlined />}
															block
															onClick={() => {
																saveCurrentFormState();
																const newCustomForm = {
																	elements: [],
																	metadata: getDefaultMetadata('custom'),
																};
															
																setFormDrafts((prevDrafts) => {
																	const updatedDrafts = { ...prevDrafts };
																	
																	const customForms = selectedUserGroup
																		? [...(prevDrafts.userGroups?.[selectedUserGroup]?.custom || [])]
																		: [...(prevDrafts.main?.custom || [])];
																	
																	customForms.push(newCustomForm);
																	
																	if (selectedUserGroup) {
																		if (!updatedDrafts.userGroups[selectedUserGroup]) {
																			updatedDrafts.userGroups[selectedUserGroup] = createEmptyFormDrafts();
																		}
																		updatedDrafts.userGroups[selectedUserGroup].custom = customForms;
																	} else {
																		updatedDrafts.main.custom = customForms;
																	}
																	
																	return updatedDrafts;
																});
															
																const customForms = selectedUserGroup
																	? formDrafts.userGroups?.[selectedUserGroup]?.custom || []
																	: formDrafts.main?.custom || [];
																
																const newIndex = customForms.length;
																setSelectedCustomFormIndex(newIndex);
																
																setData((prevData) => ({
																	...prevData,
																	elements: [],
																}));
																
																const defaultMetadata = getDefaultMetadata('custom');
																setFormMetadata(defaultMetadata);
																
																message.success('New custom form created');
															}}
														>
															Add New Form
														</Button>
													</div>
												)}
											</>
										)}
										onChange={(value) => {
											saveCurrentFormState();
											setSelectedCustomFormIndex(value);
											
											const customForms = selectedUserGroup
												? formDrafts.userGroups?.[selectedUserGroup]?.custom || []
												: formDrafts.main?.custom || [];
											
											if (customForms.length > value) {
												setData((prevData) => ({
													...prevData,
													elements: customForms[value]?.elements || [],
												}));
												setFormMetadata(customForms[value]?.metadata || getDefaultMetadata('custom'));
											}
										}}
									>
										{(selectedUserGroup
											? formDrafts.userGroups?.[selectedUserGroup]?.custom || []
											: formDrafts.main?.custom || []
										).map((form, index) => (
											<Option key={index} value={index}>
												<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
													<span>{form.metadata?.title || `Custom Form ${index + 1}`}</span>
													{isSuperAdmin && (selectedUserGroup
														? formDrafts.userGroups?.[selectedUserGroup]?.custom?.length > 1
														: formDrafts.main?.custom?.length > 1) && (
														<Button
															type="text"
															size="small"
															danger
															icon={<DeleteOutlined />}
															onClick={(e) => {
																e.stopPropagation();
																
																Modal.confirm({
																	title: 'Delete Custom Form',
																	content: 'Are you sure you want to delete this custom form? This action cannot be undone.',
																	onOk: () => {
																		const customForms = selectedUserGroup
																			? [...(formDrafts.userGroups?.[selectedUserGroup]?.custom || [])]
																			: [...(formDrafts.main?.custom || [])];
																		
																		if (customForms.length > 1) {
																			customForms.splice(index, 1);
																			
																			setFormDrafts((prevDrafts) => {
																				const updatedDrafts = { ...prevDrafts };
																				
																				if (selectedUserGroup) {
																					if (!updatedDrafts.userGroups[selectedUserGroup]) {
																						updatedDrafts.userGroups[selectedUserGroup] = createEmptyFormDrafts();
																					}
																					updatedDrafts.userGroups[selectedUserGroup].custom = customForms;
																				} else {
																					updatedDrafts.main.custom = customForms;
																				}
																				
																				return updatedDrafts;
																			});
																			
																			const newIndex = Math.min(
																				selectedCustomFormIndex === index
																					? Math.max(0, index - 1)
																					: selectedCustomFormIndex > index
																						? selectedCustomFormIndex - 1
																						: selectedCustomFormIndex,
																				customForms.length - 1
																			);
																			setSelectedCustomFormIndex(newIndex);
																			
																			if (customForms.length > 0 && customForms[newIndex]) {
																				setData((prevData) => ({
																					...prevData,
																					elements: customForms[newIndex]?.elements || [],
																				}));
																				setFormMetadata(customForms[newIndex]?.metadata || getDefaultMetadata('custom'));
																			}
																			
																			message.success('Custom form deleted');
																		} else {
																			message.error('Cannot delete the last custom form');
																		}
																	},
																});
															}}
														/>
													)}
												</div>
											</Option>
										))}
									</Select>
								</div>
							</div>
						): null}
						<div style={{ marginBottom: '16px' }}>
							<Checkbox
								checked={useUniqueFormsForGroups}
								onChange={(e) => {
									saveCurrentFormState();
									handleUniqueFormsToggle(e.target.checked);
									if (
										e.target.checked &&
										userGroups.length > 0
									) {
										setTimeout(() => {
											handleUserGroupChange(
												userGroups[0].id,
											);
										}, 0);
									}
								}}
							>
								Create unique forms for different user groups?
							</Checkbox>
						</div>

						{useUniqueFormsForGroups && (
							<div>
								<label
									style={{
										display: 'block',
										marginBottom: '8px',
									}}
								>
									Select User Group:
								</label>
								<Select
									style={{ width: '100%' }}
									placeholder="Select User Group"
									value={selectedUserGroup}
									onChange={handleUserGroupChange}
								>
									{userGroups.map((userGroup) => (
										<Option
											key={userGroup.id}
											value={userGroup.id}
										>
											{userGroup.name}
										</Option>
									))}
								</Select>
							</div>
						)}
					</Card>

					{/* Form Elements Sidebar */}
					<FormElementsSidebar onAddElement={addElement} />

					{/* Form Metadata Settings */}
					<FormMetadataSettings
						metadata={formMetadata}
						onChange={setFormMetadata}
						formType={formType}
						style={{ maxWidth: '100%' }}
						isSuperAdmin={isSuperAdmin}
					/>
				</Col>

				{/* Form editor and preview */}
				<Col span={20}>
					<Card
						title={
							<>
								<div style={{ fontWeight: 900 }} className={formType === 'custom' ? 'custom-form-editable-field' : undefined}>
									{formType === 'custom'
										? (
											<Typography.Text
												editable={{
													onChange: (value) => {
														const updatedMetadata = {
															...formMetadata,
															title: value
														};
														setFormMetadata(updatedMetadata);

														setFormDrafts((prevDrafts) => {
															const updatedDrafts = { ...prevDrafts };
															const customForms = selectedUserGroup
																? [...(prevDrafts.userGroups?.[selectedUserGroup]?.custom || [])]
																: [...(prevDrafts.main?.custom || [])];

															if (customForms[selectedCustomFormIndex]) {
																customForms[selectedCustomFormIndex] = {
																	...customForms[selectedCustomFormIndex],
																	metadata: updatedMetadata
																};

																if (selectedUserGroup) {
																	if (!updatedDrafts.userGroups[selectedUserGroup]) {
																		updatedDrafts.userGroups[selectedUserGroup] = createEmptyFormDrafts();
																	}
																	updatedDrafts.userGroups[selectedUserGroup].custom = customForms;
																} else {
																	updatedDrafts.main.custom = customForms;
																}
															}

															return updatedDrafts;
														});
													},
													triggerType: ['icon', 'text'],
													maxLength: 50,
													disabled: !isSuperAdmin
												}}
											>
												{formMetadata?.title || 'New Custom Form'}
											</Typography.Text>
										)
										: formTypeHeaders?.[formType]?.title}
								</div>
								<div
									style={{ fontSize: '14px', color: '#666' }}
									className={formType === 'custom' ? 'custom-form-editable-field' : undefined}
								>
									{formType === 'custom'
										? (
											<Typography.Paragraph
												editable={{
													onChange: (value) => {
														const updatedMetadata = {
															...formMetadata,
															description: value
														};
														setFormMetadata(updatedMetadata);
														
														setFormDrafts((prevDrafts) => {
															const updatedDrafts = { ...prevDrafts };
															const customForms = selectedUserGroup
																? [...(prevDrafts.userGroups?.[selectedUserGroup]?.custom || [])]
																: [...(prevDrafts.main?.custom || [])];
															
															if (customForms[selectedCustomFormIndex]) {
																customForms[selectedCustomFormIndex] = {
																	...customForms[selectedCustomFormIndex],
																	metadata: updatedMetadata
																};
																
																if (selectedUserGroup) {
																	if (!updatedDrafts.userGroups[selectedUserGroup]) {
																		updatedDrafts.userGroups[selectedUserGroup] = createEmptyFormDrafts();
																	}
																	updatedDrafts.userGroups[selectedUserGroup].custom = customForms;
																} else {
																	updatedDrafts.main.custom = customForms;
																}
															}
															
															return updatedDrafts;
														});
													},
													triggerType: ['icon', 'text'],
													maxLength: 500,
													autoSize: {
														maxRows: 2,
														minRows: 1,
													},
													disabled: !isSuperAdmin
												}}
											>
												{formMetadata?.description || 'Click to add a description for this custom form'}
											</Typography.Paragraph>
										)
										: formTypeHeaders?.[formType]?.description}
								</div>
							</>
						}
						style={{ borderRadius: '8px', display: 'block !important' }}
						className={"frm-b-new-main"}
						extra={
							<Space>
								<Button
									type="primary"
									icon={<SaveOutlined />}
									onClick={saveFormsToModernFormat}
								>
									Save Forms
								</Button>
								<Button type="ghost" danger onClick={clearForm}>
									Clear
								</Button>
							</Space>
						}
					>
						<Row
							gutter={16}
							style={{
								minHeight: 'calc(100vh - 200px)',
								marginLeft: 0,
								marginRight: 0,
							}}
						>
							{/* Form editor area - left side */}
							<Col
								span={12}
								style={{
									height: '100%',
									paddingLeft: 0,
									paddingRight: '24px',
								}}
							>
								<Card
									title={
										<span style={{ fontWeight: 900 }}>
											Form Editor
										</span>
									}
									size="small"
									headStyle={{ padding: 0 }}
									style={{
										height: '100%',
										overflowY: 'auto',
										border: 'none',
									}}
								>
									<FormArea
										elements={data.elements}
										onEdit={editElement}
										onDuplicate={duplicateElement}
										onRemove={removeElement}
										onAddElement={addElement}
										reorderElement={reorderElement}
										formAreaId="main-form-area"
										showCopyFromMainButton={
											useUniqueFormsForGroups &&
											selectedUserGroup &&
											data.elements.length === 0
										}
										onCopyFromMain={copyFromMainForm}
									/>
								</Card>
							</Col>

							{/* Form preview area - right side */}
							<Col span={12} style={{ height: '100%' }}>
								<FormPreview
									elements={data.elements}
									formMetadata={formMetadata}
									formType={formType}
									currentUser={currentUser}
									allMultiLingualData={allMultiLingualData}
								/>
							</Col>
						</Row>
					</Card>
				</Col>
			</Row>
			{/* Element editor modal */}
			<ElementEditor
				element={editingElement}
				onSave={handleSaveElement}
				visible={isEditorVisible}
				onCancel={() => {
					setIsEditorVisible(false);
					setEditingElement(null);
				}}
			/>
		</FormBuilderContext.Provider>
	);
}
